import { useCallback, useEffect, useRef, useState } from 'react';

/** The person closed the reason prompt; the save that asked for it does not run. */
export class VerifiedEditReasonCancelledError extends Error {
  constructor() {
    super('A reason is required to change a verified intake');
    this.name = 'VerifiedEditReasonCancelledError';
  }
}

interface PendingPrompt {
  promise: Promise<string>;
  resolve: (reason: string) => void;
  reject: (error: Error) => void;
}

/**
 * Collect the reason the API requires before a verified intake is edited
 * (GDN-4034). Editing is never blocked: saves wait on one shared prompt, and
 * the submitted reason is reused by every later save until `clear` runs.
 * Providers keep it for the session; admins clear it after each Save changes.
 */
export function useVerifiedEditReason(isVerified: boolean) {
  const isVerifiedRef = useRef(isVerified);
  const reasonRef = useRef<string | undefined>(undefined);
  const pendingRef = useRef<PendingPrompt | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    isVerifiedRef.current = isVerified;
    // A reason belongs to one verification: once the intake is unverified, a
    // later re-verification must ask again rather than reuse it.
    if (!isVerified) reasonRef.current = undefined;
  }, [isVerified]);

  // Settle any save still waiting on the prompt when the editor goes away, so
  // callers do not hang on a dialog that can no longer open.
  useEffect(
    () => () => {
      pendingRef.current?.reject(new VerifiedEditReasonCancelledError());
      pendingRef.current = null;
    },
    [],
  );

  const resolveReason = useCallback((): Promise<string | undefined> => {
    if (!isVerifiedRef.current) return Promise.resolve(undefined);
    if (reasonRef.current) return Promise.resolve(reasonRef.current);
    if (!pendingRef.current) {
      let resolve!: (reason: string) => void;
      let reject!: (error: Error) => void;
      const promise = new Promise<string>((resolvePromise, rejectPromise) => {
        resolve = resolvePromise;
        reject = rejectPromise;
      });
      // Callers await this promise; the no-op handler only stops a prompt that
      // is settled on unmount from surfacing as an unhandled rejection.
      promise.catch(() => undefined);
      pendingRef.current = { promise, resolve, reject };
      setOpen(true);
    }
    return pendingRef.current.promise;
  }, []);

  const onSubmit = useCallback((reason: string) => {
    const cleaned = reason.trim();
    if (!cleaned) return;
    reasonRef.current = cleaned;
    pendingRef.current?.resolve(cleaned);
    pendingRef.current = null;
    setOpen(false);
  }, []);

  const onCancel = useCallback(() => {
    pendingRef.current?.reject(new VerifiedEditReasonCancelledError());
    pendingRef.current = null;
    setOpen(false);
  }, []);

  const clear = useCallback(() => {
    reasonRef.current = undefined;
  }, []);

  return { resolveReason, clear, dialog: { open, onSubmit, onCancel } };
}

/** Request-body fragment for a resolved reason; empty when none is needed. */
export function verifiedEditReasonBody(reason: string | undefined) {
  return reason ? { verified_edit_reason: reason } : {};
}
